import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, UseQueryState, useSubscription } from 'urql';
import { GraphqlOperations } from '../../../../logic/graphql/operations';
import {
  Account,
  ExchangeOrderListItem,
  OrderingDirection,
  OrderingType,
  Query,
  SecurityListType,
  SecurityType,
  Status,
  Subscription,
} from '../../../../logic/graphql/types/types.d';
import { Paths } from '../../../../navigation/paths';

type UseExchangeOrderType = {
  controls: {
    handleCreateClick: () => void;
    selectedStatus: Status | 'all';
    resetList: () => void;
    setSelectedStatus: (status: Status | 'all') => void;
    selectedOrderingType: OrderingType;
    selectedOrderingDirection: OrderingDirection;
    securitiesQueryResult: UseQueryState<Query>;
    selectedSecurities: string[];
    setSelectedInstruments: (instuments: string[]) => void;
    setSelectedOrderingType: (type: OrderingType) => void;
    setSelectedOrderingDirection: (direction: OrderingDirection) => void;
  };
  list: {
    sumPortfolioLastUpdateQueryResult: UseQueryState<Query>;
    hasZeroItems: boolean;
    hasNextPage: boolean;
    itemsResultQuery: UseQueryState<Query>;
    items: readonly ExchangeOrderListItem[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetchNextPage: (startIndex: number, stopIndex: number) => any;
  };
  account: {
    userAccountsQueryResult: UseQueryState<Query>;
    userAccountOptions: readonly { value: string; label: string }[];
    selectedAccount: string;
    setSelectedAccount: (account: string) => void;
  };
};

const useListHeader = (): UseExchangeOrderType => {
  // MARK: - Resources
  const history = useHistory();

  // MARK: - State
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedSecurities, setSelectedInstruments] = useState<string[]>([]);
  const [selectedOrderingType, setSelectedOrderingType] = useState<OrderingType>(
    OrderingType.ValidityStart,
  );
  const [selectedOrderingDirection, setSelectedOrderingDirection] = useState<OrderingDirection>(
    OrderingDirection.Desc,
  );
  const [selectedStatus, setSelectedStatus] = useState<Status | 'all'>(Status.Active);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [items, setItems] = useState<ExchangeOrderListItem[]>([]);

  // MARK: - Queries
  const [sumPortfolioLastUpdateQueryResult, reexecuteSumPortfolioLastUpdateQuery] = useQuery<Query>(
    {
      query: GraphqlOperations.queries.sum_portfolioLastUpdate.queryString,
      requestPolicy: 'cache-and-network',
    },
  );

  const [itemsResultQuery, reexecuteItemsResultQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_exchange_orders.queryString,
    variables: GraphqlOperations.queries.get_exchange_orders.createVars(
      currentPage,
      10,
      selectedOrderingType,
      selectedOrderingDirection,
      selectedStatus,
      selectedSecurities,
      selectedAccount,
    ),
    requestPolicy: 'network-only',
    pause: selectedAccount === '',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  const fetchNextPage = (startIndex: number, stopIndex: number): any => {
    if (startIndex / 10 >= currentPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const userAccountsQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAccounts.queryString,
  })[0];

  const securitiesQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_securities.queryString,
    variables: GraphqlOperations.queries.get_securities.createVars(
      SecurityType.ExchangeOrder,
      SecurityListType.OwnHistory,
      selectedAccount,
    ),
    pause: selectedAccount === '',
    requestPolicy: 'network-only',
  })[0];

  // MARK: - Subscriptions

  const handleNewExchangeOrderSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionExchangeOrders: ExchangeOrderListItem[] = [],
    response: Subscription,
  ): ExchangeOrderListItem[] => {
    if (response && response.new_exchange_order) {
      resetList();
      reexecuteItemsResultQuery();
      reexecuteSumPortfolioLastUpdateQuery();
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.new_exchange_order.subscriptionString,
    },
    handleNewExchangeOrderSubscription,
  );

  const handleUpdateExchangeOrderSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionExchangeOrders: ExchangeOrderListItem[] = [],
    response: Subscription,
  ): ExchangeOrderListItem[] => {
    if (response && response.update_exchange_order) {
      const item = response.update_exchange_order;
      const itemsCopy = [...items];
      const itemToChangeIndex = itemsCopy.findIndex((i) => i.id === item.id);
      if (itemToChangeIndex >= 0) {
        itemsCopy[itemToChangeIndex] = item;
      }
      setItems(itemsCopy);
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_exchange_order.subscriptionString,
    },
    handleUpdateExchangeOrderSubscription,
  );

  // MARK: - Lifecycle methods

  useEffect(() => {
    if (
      itemsResultQuery.data?.get_exchange_orders?.page_count !== undefined &&
      itemsResultQuery.data.get_exchange_orders.page === currentPage
    ) {
      setItems([...items.concat(itemsResultQuery.data.get_exchange_orders.exchange_orders)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsResultQuery.data]);

  useEffect(() => {
    const accounts = userAccountsQueryResult.data?.getUser?.accounts;
    if (accounts && accounts[0]) {
      setSelectedAccount(accounts[0].number);
    }
  }, [userAccountsQueryResult]);

  // MARK: - Event handlers

  const handleCreateClick = (): void => {
    history.push(Paths.new_exchange_order);
  };

  // MARK: - Helpers

  const resetList = (): void => {
    setCurrentPage(1);
    setItems([]);
  };

  const hasZeroItems =
    !itemsResultQuery.fetching &&
    !!itemsResultQuery.data?.get_exchange_orders?.exchange_orders &&
    items.length === 0;

  const hasNextPage =
    itemsResultQuery.fetching ||
    (itemsResultQuery.data?.get_exchange_orders?.page_count !== undefined &&
      itemsResultQuery.data?.get_exchange_orders?.page !== undefined &&
      itemsResultQuery.data.get_exchange_orders.page_count >
        itemsResultQuery.data.get_exchange_orders.page);

  const userAccountOptions =
    userAccountsQueryResult.data?.getUser?.accounts?.map((account: Account) => ({
      value: account.number,
      label: `${account.type}: ${account.number}`,
    })) || [];

  return {
    controls: {
      handleCreateClick,
      selectedStatus,
      resetList,
      setSelectedStatus,
      selectedOrderingType,
      selectedOrderingDirection,
      securitiesQueryResult,
      selectedSecurities,
      setSelectedInstruments,
      setSelectedOrderingType,
      setSelectedOrderingDirection,
    },
    list: {
      sumPortfolioLastUpdateQueryResult,
      hasZeroItems,
      hasNextPage,
      itemsResultQuery,
      items,
      fetchNextPage,
    },
    account: {
      userAccountsQueryResult,
      userAccountOptions,
      selectedAccount,
      setSelectedAccount,
    },
  };
};

export default useListHeader;
